import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, memo } from "react";
import "./LTSearchBox.css";

interface LTSearchBoxProps {
  value?: string;
  placeholder?: string;
  maxLength?: number;
  onChange?: (value: string) => void;
  onSearch?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  autoSearch?: boolean;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

const LTSearchBox = memo<LTSearchBoxProps>(({
  value = "",
  placeholder = "Search...",
  maxLength = 30,
  onChange,
  onSearch,
  onKeyDown,
  autoSearch = false,
  className = "",
  disabled = false,
  ariaLabel = "Search input"
}) => {
  // Memoized handlers following rerender-functional-setstate
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange?.(newValue);
    
    // Auto-search when length > 2 (following client-swr-dedup pattern)
    if (autoSearch && newValue.length > 2) {
      onSearch?.();
    }
  }, [onChange, onSearch, autoSearch]);

  const handleKeyUp = useCallback(() => {
    // Auto-search trigger for typing
    if (autoSearch && value.length > 2) {
      onSearch?.();
    }
  }, [autoSearch, value.length, onSearch]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onSearch?.();
    }
    onKeyDown?.(event);
  }, [onSearch, onKeyDown]);

  const handleSearchClick = useCallback(() => {
    onSearch?.();
  }, [onSearch]);

  return (
    <div className={`lt-search-box ${className}`.trim()}>
      <div className="lt-search-box__wrapper">
        <input
          className="lt-search-box__input"
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onKeyUp={handleKeyUp}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          disabled={disabled}
          aria-label={ariaLabel}
        />
        <button
          className="lt-search-box__button"
          onClick={handleSearchClick}
          disabled={disabled}
          aria-label="Search"
          type="button"
        >
          <FontAwesomeIcon icon="search" />
        </button>
      </div>
    </div>
  );
});

LTSearchBox.displayName = "LTSearchBox";

export default LTSearchBox;